import {match} from 'ts-pattern';

import {isNil} from 'ramda';

import {getSafely} from 'shared/web';

import {VehiclePriceChangeDirection} from '../../../../../utils/getVehiclePriceChangeDirection';

export const BAR_HIGHLIGHT_PRICE_CHANGE_DEPTH = 8;
export const BAR_HIGHLIGHT_CAP_HEIGHT = 3;
export const BAR_HIGHLIGHT_HOLE_MAX_RADIUS = 5;
export const BAR_HIGHLIGHT_HOLE_MIN_RADIUS = 2;
export const BAR_HIGHLIGHT_HOLE_TOP_GAP = 5;
export const BAR_HIGHLIGHT_TOP_RADIUS = 4;
export const BAR_HIGHLIGHT_NOTCH_WIDTH_RATIO = 0.7;

interface BarRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

interface GetBarHighlightPathsParams extends BarRect {
  priceChangeDirection: VehiclePriceChangeDirection | null;
  showPrivateSellerHole: boolean;
  hasCap: boolean;
}

export interface BarHighlightPaths {
  bodyPath: string;
  capPath: string | null;
  fillRule: 'evenodd' | 'nonzero';
}

type Point = [number, number];

function point(x: number, y: number): Point {
  return [x, y];
}

function formatCoordinate(value: number) {
  return Math.round(value * 100) / 100;
}

function distance(a: Point, b: Point) {
  return Math.hypot(b[0] - a[0], b[1] - a[1]);
}

function getRoundedTopBodyPath(params: BarRect, radius: number) {
  const {x, y, width, height} = params;
  const r = Math.min(radius, width / 2, height);

  if (r <= 0) {
    return `M ${formatCoordinate(x)} ${formatCoordinate(y)} H ${formatCoordinate(x + width)} V ${formatCoordinate(y + height)} H ${formatCoordinate(x)} Z`;
  }

  return [
    `M ${formatCoordinate(x)} ${formatCoordinate(y + r)}`,
    `Q ${formatCoordinate(x)} ${formatCoordinate(y)} ${formatCoordinate(x + r)} ${formatCoordinate(y)}`,
    `H ${formatCoordinate(x + width - r)}`,
    `Q ${formatCoordinate(x + width)} ${formatCoordinate(y)} ${formatCoordinate(x + width)} ${formatCoordinate(y + r)}`,
    `V ${formatCoordinate(y + height)}`,
    `H ${formatCoordinate(x)}`,
    'Z',
  ].join(' ');
}

/**
 * Closed polygon with quadratic rounded corners.
 * Bottom corners can stay sharp via `sharpCornerIndices` (matches plain bar bottoms).
 */
function getRoundedPolygonPath(
  points: Point[],
  radius: number,
  sharpCornerIndices: ReadonlySet<number> = new Set()
) {
  if (points.length < 3) {
    return '';
  }

  const n = points.length;
  const commands: string[] = [];

  for (let i = 0; i < n; i++) {
    const prev = points[(i - 1 + n) % n];
    const curr = getSafely(points, i);
    const next = points[(i + 1) % n];

    if (isNil(prev) || isNil(curr) || isNil(next)) {
      continue;
    }

    const distPrev = distance(prev, curr);
    const distNext = distance(curr, next);

    if (sharpCornerIndices.has(i) || radius <= 0 || distPrev === 0 || distNext === 0) {
      const command = i === 0 ? 'M' : 'L';
      commands.push(`${command} ${formatCoordinate(curr[0])} ${formatCoordinate(curr[1])}`);
      continue;
    }

    const cornerRadius = Math.min(radius, distPrev / 2, distNext / 2);
    const before = point(
      curr[0] - ((curr[0] - prev[0]) / distPrev) * cornerRadius,
      curr[1] - ((curr[1] - prev[1]) / distPrev) * cornerRadius
    );
    const after = point(
      curr[0] + ((next[0] - curr[0]) / distNext) * cornerRadius,
      curr[1] + ((next[1] - curr[1]) / distNext) * cornerRadius
    );

    if (i === 0) {
      commands.push(`M ${formatCoordinate(before[0])} ${formatCoordinate(before[1])}`);
    } else {
      commands.push(`L ${formatCoordinate(before[0])} ${formatCoordinate(before[1])}`);
    }

    commands.push(
      `Q ${formatCoordinate(curr[0])} ${formatCoordinate(curr[1])} ${formatCoordinate(after[0])} ${formatCoordinate(after[1])}`
    );
  }

  commands.push('Z');
  return commands.join(' ');
}

/**
 * Top-edge silhouette for price-change encoding:
 * - increased → caret (^) peak
 * - decreased → V-shaped notch bite
 * - none → flat top
 */
function getTopEdgePoints(
  params: BarRect,
  priceChangeDirection: VehiclePriceChangeDirection | null,
  depth: number
): Point[] {
  const {x, y, width} = params;
  const midX = x + width / 2;

  return match(priceChangeDirection)
    .with('increased', () => [point(x, y + depth), point(midX, y), point(x + width, y + depth)])
    .with('decreased', () => {
      const notchHalfWidth = (width * BAR_HIGHLIGHT_NOTCH_WIDTH_RATIO) / 2;

      return [
        point(x, y),
        point(midX - notchHalfWidth, y),
        point(midX, y + depth),
        point(midX + notchHalfWidth, y),
        point(x + width, y),
      ];
    })
    .with(null, () => [point(x, y), point(x + width, y)])
    .exhaustive();
}

function getPriceChangeBodyPath(params: BarRect, topEdge: Point[], radius: number) {
  const bodyPoints: Point[] = [
    ...topEdge,
    point(params.x + params.width, params.y + params.height),
    point(params.x, params.y + params.height),
  ];
  const bottomRightIndex = topEdge.length;
  const bottomLeftIndex = topEdge.length + 1;
  // Keep the directional tip sharp (caret peak / V-notch tip); round everything else on top.
  const tipIndex = Math.floor(topEdge.length / 2);

  return getRoundedPolygonPath(
    bodyPoints,
    radius,
    new Set([tipIndex, bottomRightIndex, bottomLeftIndex])
  );
}

function lerpPoint(a: Point, b: Point, t: number): Point {
  return point(a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t);
}

function quadraticAt(p0: Point, p1: Point, p2: Point, t: number): Point {
  const oneMinusT = 1 - t;
  return point(
    oneMinusT * oneMinusT * p0[0] + 2 * oneMinusT * t * p1[0] + t * t * p2[0],
    oneMinusT * oneMinusT * p0[1] + 2 * oneMinusT * t * p1[1] + t * t * p2[1]
  );
}

/**
 * Cap for a plain (non-repriced) bar — follows the same rounded top as the body,
 * not a flat strip (which would leave sharp corners against the rounded bar).
 */
function getFlatRoundedCapPath(params: BarRect, capHeight: number, radius: number) {
  if (capHeight <= 0) {
    return null;
  }

  const {x, y, width} = params;
  const r = Math.min(radius, width / 2);
  const innerY = y + capHeight;

  if (r <= 0) {
    return [
      `M ${formatCoordinate(x)} ${formatCoordinate(y)}`,
      `H ${formatCoordinate(x + width)}`,
      `V ${formatCoordinate(innerY)}`,
      `H ${formatCoordinate(x)}`,
      'Z',
    ].join(' ');
  }

  // Cap fits the full corner arcs (same Q as the body top).
  if (capHeight >= r) {
    return [
      `M ${formatCoordinate(x)} ${formatCoordinate(innerY)}`,
      `L ${formatCoordinate(x)} ${formatCoordinate(y + r)}`,
      `Q ${formatCoordinate(x)} ${formatCoordinate(y)} ${formatCoordinate(x + r)} ${formatCoordinate(y)}`,
      `H ${formatCoordinate(x + width - r)}`,
      `Q ${formatCoordinate(x + width)} ${formatCoordinate(y)} ${formatCoordinate(x + width)} ${formatCoordinate(y + r)}`,
      `L ${formatCoordinate(x + width)} ${formatCoordinate(innerY)}`,
      'Z',
    ].join(' ');
  }

  // Cap is shorter than the corner radius — walk only the top portion of each Q.
  const leftP0 = point(x, y + r);
  const leftP1 = point(x, y);
  const leftP2 = point(x + r, y);
  const leftT = 1 - Math.sqrt(capHeight / r);
  const leftStart = quadraticAt(leftP0, leftP1, leftP2, leftT);
  const leftCtrl = lerpPoint(leftP1, leftP2, leftT);

  const rightP0 = point(x + width - r, y);
  const rightP1 = point(x + width, y);
  const rightP2 = point(x + width, y + r);
  const rightT = Math.sqrt(capHeight / r);
  const rightEnd = quadraticAt(rightP0, rightP1, rightP2, rightT);
  const rightCtrl = lerpPoint(rightP0, rightP1, rightT);

  return [
    `M ${formatCoordinate(leftStart[0])} ${formatCoordinate(leftStart[1])}`,
    `Q ${formatCoordinate(leftCtrl[0])} ${formatCoordinate(leftCtrl[1])} ${formatCoordinate(leftP2[0])} ${formatCoordinate(leftP2[1])}`,
    `H ${formatCoordinate(x + width - r)}`,
    `Q ${formatCoordinate(rightCtrl[0])} ${formatCoordinate(rightCtrl[1])} ${formatCoordinate(rightEnd[0])} ${formatCoordinate(rightEnd[1])}`,
    'Z',
  ].join(' ');
}

function getPriceChangeCapPath(topEdge: Point[], capHeight: number, radius: number) {
  if (topEdge.length < 2 || capHeight <= 0) {
    return null;
  }

  const innerEdge = topEdge.map(([px, py]) => point(px, py + capHeight));
  const capPoints: Point[] = [...topEdge, ...innerEdge.reverse()];
  const capCornerRadius = Math.min(radius, capHeight);
  const outerTipIndex = Math.floor(topEdge.length / 2);
  // Inner edge is reversed, so the tip sits at the same offset from the end of the strip.
  const innerTipIndex = topEdge.length + (topEdge.length - 1 - outerTipIndex);

  return getRoundedPolygonPath(capPoints, capCornerRadius, new Set([outerTipIndex, innerTipIndex]));
}

function getCirclePath(cx: number, cy: number, radius: number) {
  const r = formatCoordinate(radius);
  const cyf = formatCoordinate(cy);

  // Two semicircle arcs form a full circle subpath for evenodd hole cutting.
  return `M ${formatCoordinate(cx - radius)} ${cyf} a ${r} ${r} 0 1 0 ${formatCoordinate(radius * 2)} 0 a ${r} ${r} 0 1 0 ${formatCoordinate(-radius * 2)} 0`;
}

export function getBarHighlightPaths(params: GetBarHighlightPathsParams): BarHighlightPaths {
  const priceChangeDepth = params.priceChangeDirection
    ? Math.min(BAR_HIGHLIGHT_PRICE_CHANGE_DEPTH, params.height * 0.4, params.width * 0.5)
    : 0;

  const topEdge = getTopEdgePoints(params, params.priceChangeDirection, priceChangeDepth);
  const cornerRadius = Math.min(BAR_HIGHLIGHT_TOP_RADIUS, params.width / 2);

  const capHeight = params.hasCap
    ? Math.min(BAR_HIGHLIGHT_CAP_HEIGHT, Math.max(0, params.height - priceChangeDepth) * 0.35)
    : 0;

  // Reserve the top gap so diameter + gap always fit inside the bar.
  const availableHeightForHole = Math.max(
    0,
    params.height - priceChangeDepth - capHeight - BAR_HIGHLIGHT_HOLE_TOP_GAP
  );
  const holeRadiusCandidate = params.showPrivateSellerHole
    ? Math.min(BAR_HIGHLIGHT_HOLE_MAX_RADIUS, params.width / 3, availableHeightForHole / 2)
    : 0;
  const holeRadius = holeRadiusCandidate >= BAR_HIGHLIGHT_HOLE_MIN_RADIUS ? holeRadiusCandidate : 0;

  const bodyPath = params.priceChangeDirection
    ? getPriceChangeBodyPath(params, topEdge, cornerRadius)
    : getRoundedTopBodyPath(params, cornerRadius);

  const holeCy = params.y + priceChangeDepth + capHeight + holeRadius + BAR_HIGHLIGHT_HOLE_TOP_GAP;

  const holePath =
    holeRadius > 0 ? getCirclePath(params.x + params.width / 2, holeCy, holeRadius) : null;

  const capPath = params.priceChangeDirection
    ? getPriceChangeCapPath(topEdge, capHeight, cornerRadius)
    : getFlatRoundedCapPath(params, capHeight, cornerRadius);

  return {
    bodyPath: holePath ? `${bodyPath} ${holePath}` : bodyPath,
    capPath,
    fillRule: holePath ? 'evenodd' : 'nonzero',
  };
}

export function hasBarTopHighlightEncodings(params: {
  priceChangeDirection: VehiclePriceChangeDirection | null;
  showPrivateSellerHole: boolean;
  hasCap: boolean;
}) {
  return params.priceChangeDirection !== null || params.showPrivateSellerHole || params.hasCap;
}
